import * as React from 'react';
import { useT } from '../lib/i18n.js';
import { formatDate } from '../lib/properties.js';
import { useSession } from '../stores/session.js';
import type { TrialStatus } from '../lib/api.js';

/**
 * Krok TRIAL1 (zadani bod 5) — Nastaveni -> Kancelar: „Zkusebni obdobi do
 * d. m. rrrr" + „Zaplatit predplatne".
 *
 * Stav NECTE z vlastniho dotazu: bere ho ze session (`GET /api/me` ->
 * `trial`), tedy z tehoz zdroje jako pruh v hlavicce a brana zapisoveho
 * API. Dva dotazy by znamenaly dve pravdy o jedne vete.
 *
 * Tlacitko vede na EXISTUJICI platebni tok (cenik verejneho webu, odkaz
 * sklada server). Kdyz odkaz neni (`payUrl === null`, nenastaveny
 * `COMMERCE_PUBLIC_SITE_URL`), tlacitko se NEUKAZE — zadna falesna funkce.
 */
/**
 * Krok PRO1 — CO KARTA NABIZI ZA AKCI. Jedno rozhodnuti, ctyri odpovedi:
 *
 *  * `'pay'`    ... zkusebni obdobi bezi a jeste neni zaplaceno,
 *  * `'renew'`  ... predplatne je zaplacene → „Zaplatit dalsi mesic"
 *                   (predplatne se plati mesic predem a nic se nestrhava
 *                   samo, takze bez tohoto odkazu nemel makler kam kliknout
 *                   — audit 1. 11. 2026, bod 33),
 *  * `'closed'` ... checkout je za pravni pojistkou (`checkoutOpen === false`)
 *                   → SROZUMITELNA HLASKA, zadny odkaz. Odkaz by vedl na
 *                   obrazovku, kterou server odmitne 409
 *                   (`commerce/legal-not-approved`), tedy na chybu,
 *  * `null`     ... kancelar zkusebni obdobi nema / neni kam odkazovat.
 *
 * Chybejici `checkoutOpen` (starsi API) znamena „neomezovat": pojistka je
 * stejne na serveru, UI ji jen neopisuje.
 */
export type OfficeTrialAction = 'pay' | 'renew' | 'closed' | null;

export function officeTrialAction(trial: TrialStatus | null): OfficeTrialAction {
  if (trial === null) return null;
  if (trial.checkoutOpen === false) return 'closed';
  // Bez odkazu neni co nabidnout (`payUrl === null`) — zadne vymyslene URL.
  if (trial.payUrl === null) return null;
  /*
   * NA `endsAt` SE TU NESMI VAZAT (zivy nalez PRO1 na nasazene verzi):
   * `trial.endsAt === null` znamena „tahle kancelar zkusebni obdobi nema"
   * (zalozena jinak nez aktivaci z webu) — ale PREDPLATNE plati stejne jako
   * ostatni. Kdyz se akce vazala na `endsAt`, takova kancelar nemela kde
   * zaplatit dalsi mesic VUBEC (demo kancelar na ostre adrese je presne
   * tenhle pripad: `endsAt: null`, `paid: true`).
   */
  return trial.paid ? 'renew' : 'pay';
}

export function OfficeTrialCard(): React.JSX.Element | null {
  const t = useT();
  const trial = useSession((s) => s.trial);

  // Bez odpovedi serveru karta nic netvrdi (starsi nasazeni pole neposila).
  if (trial === null) return null;
  const action = officeTrialAction(trial);
  const payUrl = trial.payUrl;

  const body = ((): string => {
    // Krok PRO1: „zaplaceno" plati i pro kancelar BEZ zkusebniho obdobi —
    // jinak by karta nad odkazem „Zaplatit dalsi mesic" tvrdila „tahle
    // kancelar zkusebni obdobi nema", coz o predplatnem nerika nic.
    if (trial.paid) return t('webAdmin.settings.officeTrial.paid');
    if (trial.endsAt === null) return t('webAdmin.settings.officeTrial.none');
    const date = formatDate(trial.endsAt);
    if (trial.readOnly) return t('webAdmin.settings.officeTrial.expired', { date });
    return t('webAdmin.settings.officeTrial.until', { date });
  })();

  return (
    <div
      className="rh-card"
      style={{ maxWidth: 640, padding: 16, marginBottom: 16 }}
      data-testid="nastaveni-office-trial-card"
    >
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.officeTrial.title')}</h2>
      <p className="mb-2 text-sm text-gray-600" data-testid="office-trial-state">
        {body}
      </p>
      {trial.paid || trial.endsAt === null ? null : (
        <p className="mb-2 text-sm text-gray-600">{t('webAdmin.settings.officeTrial.noCard')}</p>
      )}
      {action === 'renew' ? (
        <p className="mb-2 text-sm text-gray-600">
          {t('webAdmin.settings.officeTrial.renewNote')}
        </p>
      ) : null}
      {action === 'closed' ? (
        <p className="text-sm text-gray-600" data-testid="office-trial-checkout-closed">
          {t('webAdmin.settings.officeTrial.checkoutClosed')}
        </p>
      ) : null}
      {(action === 'pay' || action === 'renew') && payUrl !== null ? (
        <a
          className="text-sm underline"
          href={payUrl}
          rel="noreferrer"
          data-testid={action === 'renew' ? 'office-trial-renew' : 'office-trial-pay'}
        >
          {t(
            action === 'renew'
              ? 'webAdmin.settings.officeTrial.renew'
              : 'webAdmin.settings.officeTrial.pay',
          )}
        </a>
      ) : null}
    </div>
  );
}
